import type { PageHelp } from '../../help/types';

/** Page guide for `/packages/:code/suggestions` — the package owner's inbox (spec §7.7, §9.3, §9.5). */
export const suggestionsHelp: PageHelp = {
  title: 'Suggestions',
  intro:
    'Wording proposed by projects that use this package’s keys. Accepting a proposal changes the value for every subscribing project, so each one is a decision for the package owner.',
  sections: ['The inbox', 'Deciding', 'Decision dialog'],
  contexts: {
    'decision-dialog': 'Click “Accept” or “Reject” on an open suggestion.',
  },
  tips: [
    'A suggestion is created when someone edits a package key from a project without the owner role on the package: the edit is stored here instead of being written.',
    'Only a package owner or an administrator sees the Accept and Reject buttons. Everyone else can read the suggestions, and the page says so above the list.',
  ],
  steps: [
    {
      selector: '[data-help="suggestions-status"]',
      title: 'Filter by status',
      body: 'Open is the inbox of undecided proposals. Accepted and Rejected keep closed suggestions readable, each with who decided it and when.',
      section: 'The inbox',
      i18n: {
        'de-AT': {
          title: 'Nach Status filtern',
          body: 'Open ist der Eingang der noch nicht entschiedenen Vorschläge. Accepted und Rejected halten abgeschlossene Vorschläge lesbar, jeweils mit der Person, die entschieden hat, und dem Zeitpunkt.',
        },
        'sr-Latn-RS': {
          title: 'Filtriranje po statusu',
          body: 'Open je prijemno sanduče predloga o kojima još nije odlučeno. Accepted i Rejected čuvaju zatvorene predloge čitljivim, uz to ko je i kada odlučio.',
        },
      },
    },
    {
      selector: '[data-help="suggestions-item"]',
      title: 'Compare now and proposed',
      body: 'Each card is one cell: the key, its locale, plural form and context where they apply, the project it came from, and who proposed it when. “Package now” is what the package says today, “Proposed” is the project’s wording. A proposal is often better wording, but sometimes it only fits the application it came from, and then the answer is to reject it with a note.',
      section: 'The inbox',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Aktuell und Vorschlag vergleichen',
          body: 'Jede Karte ist eine Zelle: der Schlüssel, seine Sprache, Pluralform und Kontext, wo zutreffend, das Projekt, aus dem er kommt, sowie wer ihn wann vorgeschlagen hat. „Package now“ ist der heutige Wert des Pakets, „Proposed“ die Formulierung des Projekts. Oft ist der Vorschlag die bessere Formulierung, manchmal passt er aber nur zur Anwendung, aus der er kommt; dann lehnen Sie ihn mit einer Notiz ab.',
        },
        'sr-Latn-RS': {
          title: 'Poređenje trenutnog i predloženog',
          body: 'Svaka kartica je jedna ćelija: ključ, njegov jezik, oblik množine i kontekst kada postoje, projekat iz kog je stigao, kao i ko ga je i kada predložio. „Package now“ je ono što paket danas kaže, „Proposed“ je formulacija projekta. Predlog je često bolja formulacija, ali ponekad odgovara samo aplikaciji iz koje je stigao, i tada ga odbijte uz napomenu.',
        },
      },
    },
    {
      selector: '[data-help="suggestions-accept"]',
      title: 'Accept a proposal',
      body: 'Opens a confirmation with an optional note. On confirm, the proposed value becomes the package translation in every subscribing project. It is stored as draft, like any other human edit.',
      section: 'Deciding',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Vorschlag annehmen',
          body: 'Öffnet eine Bestätigung mit optionaler Notiz. Nach dem Bestätigen wird der vorgeschlagene Wert zur Paketübersetzung in jedem abonnierenden Projekt. Er wird als draft gespeichert, wie jede andere Bearbeitung durch eine Person.',
        },
        'sr-Latn-RS': {
          title: 'Prihvatanje predloga',
          body: 'Otvara potvrdu sa opcionom napomenom. Posle potvrde predložena vrednost postaje prevod paketa u svakom projektu pretplatniku. Čuva se kao draft, kao i svaka druga izmena koju napravi osoba.',
        },
      },
    },
    {
      selector: '[data-help="suggestions-reject"]',
      title: 'Reject a proposal',
      body: 'Opens a confirmation with an optional note. On confirm, the proposal is closed and nothing changes: the package, and every project with it, keeps what it already says. A note saying why spares the proposer the guessing.',
      section: 'Deciding',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Vorschlag ablehnen',
          body: 'Öffnet eine Bestätigung mit optionaler Notiz. Nach dem Bestätigen wird der Vorschlag geschlossen und nichts ändert sich: Das Paket und mit ihm jedes Projekt behält den bisherigen Wert. Eine Notiz mit dem Grund erspart der vorschlagenden Person das Rätseln.',
        },
        'sr-Latn-RS': {
          title: 'Odbijanje predloga',
          body: 'Otvara potvrdu sa opcionom napomenom. Posle potvrde predlog se zatvara i ništa se ne menja: paket, a sa njim i svaki projekat, zadržava postojeću vrednost. Napomena sa razlogom poštedeće predlagača nagađanja.',
        },
      },
    },
    {
      selector: '[data-help="suggestions-note"]',
      title: 'Explain the decision',
      body: 'Optional. The note is kept with the suggestion and shown on its card under Accepted or Rejected. When you write one, it replaces the note the proposer left; left empty, theirs stays.',
      section: 'Decision dialog',
      context: 'decision-dialog',
      i18n: {
        'de-AT': {
          title: 'Entscheidung begründen',
          body: 'Optional. Die Notiz bleibt beim Vorschlag und erscheint auf seiner Karte unter Accepted oder Rejected. Schreiben Sie eine, ersetzt sie die Notiz der vorschlagenden Person; bleibt das Feld leer, bleibt deren Notiz erhalten.',
        },
        'sr-Latn-RS': {
          title: 'Obrazloženje odluke',
          body: 'Opciono. Napomena ostaje uz predlog i prikazuje se na njegovoj kartici pod Accepted ili Rejected. Ako je napišete, ona zamenjuje napomenu predlagača; ako polje ostane prazno, njegova napomena ostaje.',
        },
      },
    },
    {
      selector: '[data-help="suggestions-confirm"]',
      title: 'Confirm the decision',
      body: 'Applies the decision and closes the dialog; Cancel closes it without deciding. A suggestion that someone else has decided in the meantime is refused rather than decided twice.',
      section: 'Decision dialog',
      context: 'decision-dialog',
      i18n: {
        'de-AT': {
          title: 'Entscheidung bestätigen',
          body: 'Wendet die Entscheidung an und schließt den Dialog; „Cancel“ schließt ihn, ohne zu entscheiden. Einen Vorschlag, über den inzwischen jemand anderer entschieden hat, lehnt der Server ab, statt ihn doppelt zu entscheiden.',
        },
        'sr-Latn-RS': {
          title: 'Potvrda odluke',
          body: 'Primenjuje odluku i zatvara dijalog; „Cancel“ ga zatvara bez odluke. Predlog o kome je u međuvremenu odlučio neko drugi server odbija umesto da se o njemu odluči dva puta.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Vorschläge',
      intro:
        'Formulierungen, die Projekte für die Schlüssel dieses Pakets vorschlagen. Das Annehmen eines Vorschlags ändert den Wert für jedes abonnierende Projekt; jeder Vorschlag ist daher eine Entscheidung des Paket-Owners.',
      sections: {
        'The inbox': 'Der Eingang',
        Deciding: 'Entscheiden',
        'Decision dialog': 'Entscheidungsdialog',
      },
      contexts: {
        'decision-dialog': 'Klicken Sie bei einem offenen Vorschlag auf „Accept“ oder „Reject“.',
      },
      tips: [
        'Ein Vorschlag entsteht, wenn jemand ohne die Rolle Owner im Paket einen Paketschlüssel aus einem Projekt heraus bearbeitet: Die Bearbeitung landet hier, statt geschrieben zu werden.',
        'Nur Paket-Owner und Administratoren sehen die Schaltflächen „Accept“ und „Reject“. Alle anderen können die Vorschläge lesen, und die Seite weist über der Liste darauf hin.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Predlozi',
      intro:
        'Formulacije koje predlažu projekti koji koriste ključeve ovog paketa. Prihvatanje predloga menja vrednost za svaki projekat pretplatnik, pa je svaki predlog odluka ownera paketa.',
      sections: {
        'The inbox': 'Prijemno sanduče',
        Deciding: 'Odlučivanje',
        'Decision dialog': 'Dijalog odluke',
      },
      contexts: {
        'decision-dialog': 'Kliknite na „Accept“ ili „Reject“ kod otvorenog predloga.',
      },
      tips: [
        'Predlog nastaje kada neko bez uloge owner na paketu izmeni ključ paketa iz projekta: izmena se čuva ovde umesto da bude upisana.',
        'Samo owner paketa ili administrator vidi dugmad „Accept“ i „Reject“. Svi ostali mogu da čitaju predloge, a stranica to navodi iznad liste.',
      ],
    },
  },
};
